/**
 * Article quick-share (Jared, 2026-10-16). On every research article: the headline, the subcopy, then a row ("Share with
 * your friends and get free gifts, only for the month of October", with a small (i)), then the read-time line. The row
 * opens one pop-up that does it all in one window:
 *   1. "Invite friends, get free gifts": the referral ladder as picture cards to swipe through, "October only…", then
 *      "To share and get your personal invite link, first create your account" and the email box. "Already have an
 *      account? Click here" turns the same box into a sign-in;
 *   2. the 6-digit code from the email (or the button in that email, tapped in this browser);
 *   3. "Welcome in!", a quick summary of the ladder, then the message with their invite link (the article itself +
 *      ?ref=; one tap copies it all), "Try another message", and the share card's buttons (Jared, Oct 18): Text | Email,
 *      Facebook | More, then Copy my link. "More" opens the share card's own panel of every other app, never the
 *      phone's share sheet; "Terms" after the deadline line opens the share card's picture ladder;
 *   4. after a share: "Awesome job! That's one person you just taught about microplastics. Who's next?" with the same
 *      buttons.
 *
 * - Not a second signup flow. The pop-up's form is bound to the page's signup adapter (lifestyleLanding.tsx bindForm), so
 *   the availability check, the security check, the typo prompt, the friend's referral credit, attribution and every
 *   tracking event are the ones each article form uses, and the code is checked by the same verifyCode as the
 *   "Go check your email" pop-up. The sign-in uses the adapter's own request for a sign-in link.
 * - Signed-in members go straight to the message and the buttons.
 * - One experiment, one variable: the row's line (ROW_LINES), 50/50 per browser and kept in localStorage. Every event
 *   from the row and the pop-up carries `variant`.
 * - Where no signup service answers (a Netlify deploy preview), the pop-up runs a marked preview path that sends nothing,
 *   so the whole flow can be tapped through on a phone. Never on the live site's own hosts.
 * - Light DOM, because the security check (Cloudflare Turnstile) renders inside the form. The form never moves: the
 *   first step stays in place (hidden) while the others show, and the pop-up is hidden, not removed, when it closes, so
 *   the check's frame is never reloaded. Every rule is under #aelo-sp, and every name is neutral: ad and social blockers
 *   hide "share", "social", "popup", "newsletter" and "fb-" names.
 * - Like the check-email pop-up: the cookie notice and privacy controls (z-index 100 and 101) stay above it and usable;
 *   the rest of the page is inert while it is open; Esc, the × and the backdrop close it; big targets, plain words.
 * - Back does what Esc does (the share card's panel first, then the pop-up), and the article stays: one history entry
 *   while the pop-up is open, taken back when it closes any other way (Back used to leave the article).
 */
import { cleanCode, inboxTarget, type CodeOutcome, type ResendOutcome } from './checkEmailDialog'
import {
  CHANNEL_LABELS, COPY, FORM_ABOVE, LADDER, ROW_LINES, SITE,
  articleInviteLink, articleLine, articleTexts, chooseVariant, invitationsOpen, isLiveHost, sentCountText,
  type MainChannel, type RowVariant, type Rung, type ShareChannel,
} from './articleShareCopy'
// Share card v3 (PR #35): the apps, their links and order, the More and Terms pop-ups and the copy rules, so this pop-up
// and the share card send the same way.
import {
  BRAND, GRID, SHARE_CARD_COPY, SHARE_CARD_STYLE, SHARE_CHANNEL, TERMS_COPY,
  allAppsTile, appLink, cardSheet, copyBySelection, copyText as copyToClipboard, hasShareMenu, mainChannels, pasteLine, shareApps,
  shareEnvironment, termsContent,
  type ShareApp,
} from './shareCard'
import { messageFor, nextText, quickTargets, shareContentFor, type ShareContent, type ShareText } from './shareContent'
import type { FoundingCampaignConfig, FoundingSignupResult } from './types'

export type ArticleShareHost = {
  signal: AbortSignal
  /** Binds the pop-up's form to the page's signup adapter; false if the form could not be bound. */
  bindForm: (form: HTMLFormElement, hooks: {
    onResult: (email: string, result: FoundingSignupResult, warning: string | null) => void
    properties: () => Record<string, string>
  }) => boolean
  /** Forgets the address just used ("Use a different email"). */
  differentEmail: () => void
  verify: (email: string, code: string) => Promise<CodeOutcome>
  /** Sends the email again (a new code, or a sign-in link for a member) through the adapter's own request. */
  resend: (email: string) => Promise<ResendOutcome | null>
  /** The signed-in member's invite link; 'locked' when sharing is paused; null when nobody is signed in (or unknown). */
  member: () => Promise<{ referralUrl: string } | 'locked' | null>
  /** The email's button confirmed them in this browser while the pop-up waited for the code. */
  confirmed: () => void
  campaign: () => FoundingCampaignConfig | null
  serviceFailed: () => boolean
  now: () => number
  onAvailability: (listener: () => void) => () => void
  capture: (event: string, properties: Record<string, unknown>, options?: { sendInstantly?: boolean }) => void
  shared: (channel: ShareChannel, properties: Record<string, string>) => void
}

type Step = 'loading' | 'intro' | 'code' | 'back' | 'welcome' | 'share' | 'sent' | 'locked' | 'nolink' | 'closed'
type Member = { referralUrl: string } | 'locked' | null

const FORM_ID = 'article-invite-join'
const VARIANT_KEY = 'aelo_share_row_test_v1'
const MEMBER_HINT_KEY = 'aelo_member_hint_v1'
const PREVIEW_CODE = 'PREVIEW1'

// Back closes the pop-up instead of leaving the article (as in checkEmailDialog.ts). The listener is added on first use.
let onBackRequest: (() => void) | null = null
let ignoredPops = 0
let popListener = false
let popupEntries = 0

const STYLE = `
body.aelo-sp-open{overflow:hidden}
#aelo-sp[hidden]{display:none!important}
#aelo-sp .sp-shell{position:fixed;inset:0;z-index:98;display:flex;align-items:center;justify-content:center;box-sizing:border-box;padding:16px calc(var(--sp-reserve-right,0px) + 16px) calc(var(--sp-reserve,0px) + 16px) 16px;font-family:Manrope,'Avenir Next',system-ui,-apple-system,'Segoe UI',Roboto,sans-serif;font-size:17px;line-height:1.5;font-weight:400;color:#092e3e;-webkit-text-size-adjust:100%;text-size-adjust:100%;-webkit-font-smoothing:antialiased;text-align:left;letter-spacing:normal}
#aelo-sp .sp-shell *{box-sizing:border-box}
#aelo-sp .sp-backdrop{position:absolute;inset:0;background:rgba(9,46,62,.6);-webkit-backdrop-filter:blur(4px);backdrop-filter:blur(4px)}
#aelo-sp .sp-card{position:relative;width:100%;max-width:460px;max-height:100%;overflow-y:auto;overscroll-behavior:contain;padding:40px 22px 22px;background:#fff;box-shadow:0 28px 70px -18px rgba(9,46,62,.55),0 2px 8px rgba(9,46,62,.14);outline:0}
#aelo-sp :is(h2,h3,p,ul,li,figure,label){margin:0;padding:0;max-width:none;font:inherit;color:inherit;letter-spacing:normal;text-transform:none;text-wrap:pretty}
#aelo-sp ul{list-style:none}
#aelo-sp .sp-title{margin:0 40px 12px 0;font-size:30px;font-weight:750;line-height:1.08;letter-spacing:-.035em;color:#092e3e;text-wrap:balance}
#aelo-sp .sp-line{margin:0 0 12px;font-size:17px;line-height:1.5;color:#2b4752}
#aelo-sp .sp-addr{display:inline-block;max-width:100%;font-weight:700;color:#092e3e;overflow-wrap:anywhere}
#aelo-sp .sp-addr strong{font-weight:700}
#aelo-sp .sp-note-line{margin:6px 0 0;font-size:17px;font-weight:650;line-height:1.4;color:#092e3e}
#aelo-sp .sp-fine{margin:10px 0 0;font-size:14px;line-height:1.4;color:#4a6670}
#aelo-sp .sp-ask{margin:14px 0 10px;padding-top:12px;border-top:1px solid #e3e9eb;font-size:18px;font-weight:750;line-height:1.3;color:#092e3e}
#aelo-sp .signup-row{display:flex;flex-direction:column;gap:10px}
#aelo-sp input[type=email]{display:block;width:100%;min-height:58px;margin:0;padding:14px 16px;border:2px solid #6b7175;border-radius:0;background:#fff;color:#092e3e;font:inherit;font-size:18px;line-height:1.3}
#aelo-sp input[type=email]::placeholder{color:#5c6266;opacity:1}
#aelo-sp input[type=email][aria-invalid=true]{border-color:#8c1d2e}
#aelo-sp .sp-primary{display:flex;align-items:center;justify-content:center;gap:10px;width:100%;min-height:58px;margin:0;padding:14px 20px;border:0;border-radius:0;background:#092e3e;color:#fff;font:inherit;font-size:18px;font-weight:700;line-height:1.2;text-align:center;text-decoration:none;cursor:pointer;-webkit-tap-highlight-color:transparent}
#aelo-sp .sp-primary:hover{background:#164b60}
#aelo-sp .sp-primary:disabled{opacity:.5;cursor:default}
#aelo-sp .sp-primary[hidden]{display:none}
#aelo-sp .sp-secondary{display:flex;align-items:center;justify-content:center;gap:10px;width:100%;min-height:54px;margin:12px 0 0;padding:12px 18px;border:2px solid #092e3e;border-radius:0;background:#fff;color:#092e3e;font:inherit;font-size:17px;font-weight:700;line-height:1.2;text-align:center;text-decoration:none;cursor:pointer;-webkit-tap-highlight-color:transparent}
#aelo-sp :is(.sp-primary,.sp-secondary) svg{width:18px;height:18px;flex:none}
#aelo-sp .sp-helper{margin:8px 0 0;font-size:15px;line-height:1.45;color:#4a6670}
#aelo-sp .sp-switch{display:flex;flex-wrap:wrap;align-items:center;gap:0 6px;margin:2px 0 0;font-size:16px;color:#2b4752}
#aelo-sp :is(.sp-status,.form-status){margin:10px 0 0;font-size:16px;line-height:1.45;color:#2b4752;overflow-wrap:anywhere}
#aelo-sp :is(.sp-status,.form-status):empty{display:none}
#aelo-sp :is(.sp-status,.form-status):is([data-tone=error],.is-error){color:#8c1d2e;font-weight:600}
#aelo-sp .form-status[data-typo]{font-size:19px;font-weight:700;color:#092e3e}
#aelo-sp .lifestyle-live-actions{display:flex;flex-wrap:wrap;gap:10px;margin:10px 0 0}
#aelo-sp .lifestyle-live-actions:empty{display:none}
#aelo-sp .lifestyle-live-actions button{min-height:48px;margin:0;padding:10px 16px;border:2px solid #092e3e;border-radius:0;background:#fff;color:#092e3e;font:inherit;font-size:16px;font-weight:700;text-decoration:none;cursor:pointer}
#aelo-sp .lifestyle-live-actions button.is-strong:first-child{background:#092e3e;color:#fff}
#aelo-sp .lifestyle-live-actions a{color:#092e3e;font-size:15px;text-decoration:underline;text-underline-offset:4px}
#aelo-sp .lifestyle-live-security{max-width:100%;overflow:hidden;margin-top:6px}
#aelo-sp .lifestyle-live-security .founding-turnstile[data-passed=true]>div:first-child{display:none}
#aelo-sp .sp-code label{display:block;margin:6px 0 8px;font-size:17px;font-weight:700;color:#092e3e}
#aelo-sp .sp-code input{display:block;width:100%;min-height:68px;margin:0;padding:8px 12px;border:2px solid #092e3e;border-radius:0;background:#fff;color:#092e3e;font:inherit;font-size:34px;font-weight:700;letter-spacing:.3em;text-align:center;font-variant-numeric:tabular-nums}
#aelo-sp .sp-code input::placeholder{color:#b9c6ca}
#aelo-sp .sp-code input[aria-invalid=true]{border-color:#8c1d2e}
#aelo-sp .sp-code .sp-primary{margin-top:12px}
#aelo-sp .sp-note{margin:12px 0 0;padding:12px 14px;background:#fff6e5;color:#5c3b00;font-size:15px;line-height:1.45}
#aelo-sp .sp-note:empty{display:none}
#aelo-sp .sp-more{display:flex;flex-wrap:wrap;align-items:center;gap:0 18px;margin:16px 0 0;padding-top:10px;border-top:1px solid #e3e9eb}
#aelo-sp .sp-more>p:first-child{flex-basis:100%;margin:0 0 2px;font-size:15px;color:#4a6670}
#aelo-sp .sp-more .sp-status{flex-basis:100%}
#aelo-sp .sp-link{display:inline-flex;align-items:center;min-height:44px;margin:0;padding:0;border:0;background:none;color:#092e3e;font:inherit;font-size:16px;font-weight:700;text-decoration:underline;text-decoration-thickness:1px;text-underline-offset:4px;cursor:pointer;-webkit-tap-highlight-color:transparent}
#aelo-sp .sp-link:disabled{opacity:.6;cursor:wait}
#aelo-sp .sp-badge{display:grid;place-items:center;width:52px;height:52px;margin:0 0 12px;border-radius:50%;background:#d9f0e2;color:#0f5a32}
#aelo-sp .sp-badge svg{width:26px;height:26px}
/* The ladder as cards to swipe through, in the new homepage's style (Nichole's design): grey cards, a navy count. */
#aelo-sp .sp-cards{display:flex;gap:12px;margin:4px -22px 0;padding:2px 22px 6px;overflow-x:auto;overscroll-behavior-x:contain;scroll-snap-type:x mandatory;scroll-padding:0 22px;-webkit-overflow-scrolling:touch;scrollbar-width:none}
#aelo-sp .sp-cards::-webkit-scrollbar{display:none}
#aelo-sp .sp-gift{flex:0 0 74%;max-width:260px;scroll-snap-align:start;display:flex;flex-direction:column;gap:8px;padding:12px 12px 14px;border-radius:22px;background:#ebebeb}
/* The last card can't reach the start (nothing comes after it), so it snaps to the end: the 4th dot then shows the 4th card
   (Jared, Oct 17: it highlighted the 3rd). */
#aelo-sp .sp-gift:last-child{scroll-snap-align:end}
#aelo-sp .sp-gift-art{display:flex;align-items:center;justify-content:center;width:100%;height:86px}
#aelo-sp .sp-gift-art img{display:block;width:auto;height:100%;max-width:100%;object-fit:contain}
#aelo-sp .sp-gift-head{display:flex;align-items:center;gap:10px}
#aelo-sp .sp-gift-head span:last-child{display:grid;gap:1px;font-size:13px;font-weight:700;line-height:1.2;color:#4a5458}
#aelo-sp .sp-gift-head h3{font-size:17px;font-weight:800;line-height:1.15;letter-spacing:-.015em;color:#151515}
#aelo-sp .sp-dot{display:grid;place-items:center;flex:none;width:36px;height:36px;border-radius:50%;background:#092e3e;color:#fff;font-size:14px;font-weight:800;font-variant-numeric:tabular-nums}
#aelo-sp .sp-cash{display:grid;align-content:space-between;width:132px;height:80px;padding:7px 9px;background:#092e3e;color:#fff;box-shadow:0 10px 20px -12px rgba(0,0,0,.6)}
#aelo-sp .sp-cash small{font-size:7px;font-weight:700;letter-spacing:.16em;text-transform:uppercase;opacity:.8}
#aelo-sp .sp-cash b{font-size:34px;font-weight:300;line-height:1;letter-spacing:-.04em}
#aelo-sp .sp-cash i{font-style:normal;font-size:12px;font-weight:800;letter-spacing:-.04em}
#aelo-sp .sp-gift p{font-size:14px;line-height:1.35;color:#2b2b2b}
#aelo-sp .sp-value{display:flex;align-items:flex-start;gap:8px;margin-top:auto!important;padding:5px 8px 5px 5px;background:#fff;color:#092e3e!important;font-size:13px!important;font-weight:750;line-height:1.25!important}
#aelo-sp .sp-free{flex:none;padding:3px 7px 2px;background:#092e3e;color:#fff;font-size:11px;font-weight:800;letter-spacing:.1em;text-transform:uppercase}
#aelo-sp .sp-pager{display:flex;justify-content:center;gap:4px;margin:2px 0 0}
#aelo-sp .sp-pager button{display:grid;place-items:center;width:30px;height:30px;margin:0;padding:0;border:0;background:none;cursor:pointer}
#aelo-sp .sp-pager button::before{content:"";width:9px;height:9px;border-radius:50%;background:#c3ccce}
#aelo-sp .sp-pager button[aria-current=true]::before{background:#092e3e}
/* After the code: the ladder in one glance. */
#aelo-sp .sp-sum{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:8px;margin:0 0 14px}
#aelo-sp .sp-sum li{display:flex;align-items:center;gap:8px;min-width:0;padding:6px 8px;border-radius:14px;background:#ebebeb;font-size:14px;font-weight:700;line-height:1.2;color:#151515}
#aelo-sp .sp-sum .sp-dot{width:30px;height:30px;font-size:12px}
#aelo-sp .sp-lead{margin:0 0 8px;font-size:15px;font-weight:700;color:#4a6670}
#aelo-sp .sp-bubble{display:block;width:100%;margin:0 0 12px;padding:12px 14px;border:0;border-radius:18px 18px 18px 6px;background:#e9edf0;color:#16252b;font:inherit;font-size:16px;line-height:1.45;text-align:left;overflow-wrap:anywhere;cursor:copy;-webkit-tap-highlight-color:transparent}
#aelo-sp .sp-bubble:hover{background:#e1e7ea}
#aelo-sp .sp-bubble[data-copied]{box-shadow:0 0 0 3px #2f7d55 inset}
#aelo-sp .sp-tools{display:flex;justify-content:flex-end;margin:-8px 0 6px}
#aelo-sp .sp-another{display:inline-flex;align-items:center;gap:6px;min-height:44px;margin:0;padding:0 4px;border:0;background:none;color:#092e3e;font:inherit;font-size:16px;font-weight:700;text-decoration:underline;text-underline-offset:3px;cursor:pointer;-webkit-tap-highlight-color:transparent}
#aelo-sp .sp-another svg{width:17px;height:17px;flex:none}
#aelo-sp .sp-terms{position:relative;display:inline;margin:0;padding:0;border:0;background:none;color:#092e3e;font:inherit;font-weight:800;text-decoration:underline;text-underline-offset:3px;cursor:pointer;-webkit-tap-highlight-color:transparent}
/* A 44px tap area around the short word, as on the share card. */
#aelo-sp .sp-terms::after{content:"";position:absolute;inset:-12px -8px}
#aelo-sp .sp-sheets{display:contents}
#aelo-sp .sp-url{color:#0b5bd3;text-decoration:underline;text-underline-offset:3px}
#aelo-sp .sp-grid{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:10px}
#aelo-sp .sp-go{display:flex;align-items:center;justify-content:flex-start;gap:10px;min-height:58px;margin:0;padding:10px 12px;border:2px solid #092e3e;border-radius:0;background:#fff;color:#092e3e;font:inherit;font-size:17px;font-weight:750;line-height:1.15;text-align:left;text-decoration:none;cursor:pointer;-webkit-tap-highlight-color:transparent}
#aelo-sp .sp-go:hover{background:#f1f5f6}
#aelo-sp .sp-go svg{width:24px;height:24px;flex:none}
#aelo-sp .sp-go.is-main{background:#092e3e;color:#fff}
#aelo-sp .sp-go.is-main:hover{background:#164b60}
#aelo-sp .sp-go.is-wide{grid-column:1/-1;justify-content:center}
#aelo-sp .sp-count{margin:14px 0 0;font-size:16px;font-weight:800;line-height:1.45;color:#2f7d55}
#aelo-sp .sp-count span{font-weight:600;color:#4a6670}
#aelo-sp .sp-preview{display:inline-block;margin:-16px 40px 10px 0;padding:5px 10px;background:#fff6e5;color:#5c3b00;font-size:13px;font-weight:700;line-height:1.35}
#aelo-sp .sp-preview[hidden]{display:none}
#aelo-sp .sp-close{position:absolute;top:6px;right:6px;z-index:1;display:grid;place-items:center;width:44px;height:44px;margin:0;padding:0;border:0;background:none;color:#092e3e;cursor:pointer;-webkit-tap-highlight-color:transparent}
#aelo-sp .sp-close svg{width:22px;height:22px}
#aelo-sp :is(.sp-primary,.sp-secondary,.sp-go,.sp-link,.sp-close,.sp-bubble,.sp-another,.sp-terms,.sp-pager button,.lifestyle-live-actions button):focus-visible{outline:3px solid #2f7d55;outline-offset:2px}
#aelo-sp :is(input[type=email],.sp-code input):focus{outline:3px solid #2f7d55;outline-offset:2px}
#aelo-sp .sp-cards:focus-visible{outline:3px solid #2f7d55;outline-offset:2px}
#aelo-sp .sp-sr{position:absolute;width:1px;height:1px;margin:-1px;padding:0;overflow:hidden;clip:rect(0 0 0 0);white-space:nowrap;border:0}
#aelo-sp .sp-shell[data-state=closing]{pointer-events:none}
@media (min-width:600px){#aelo-sp .sp-card{padding:52px 40px 28px}#aelo-sp .sp-cards{margin:4px -40px 0;padding:2px 40px 6px;scroll-padding:0 40px}#aelo-sp .sp-gift{flex-basis:46%}#aelo-sp .sp-title{font-size:34px}}
#aelo-sp .sp-shell[data-fit=tight] .sp-card{padding-top:18px}
#aelo-sp .sp-shell[data-fit=tight] .sp-title{font-size:26px;margin-bottom:8px}
#aelo-sp .sp-shell[data-fit=tight] .sp-ask{font-size:17px}
#aelo-sp .sp-shell[data-fit=tight] .sp-badge{display:none}
@media (prefers-reduced-motion:no-preference){
#aelo-sp .sp-shell[data-state=open] .sp-backdrop{animation:sp-fade .2s ease-out both}
#aelo-sp .sp-shell[data-state=open] .sp-card{animation:sp-rise .34s cubic-bezier(.23,1,.32,1) both}
#aelo-sp .sp-step{animation:sp-step .28s cubic-bezier(.23,1,.32,1) both}
#aelo-sp .sp-badge{animation:sp-pop .45s cubic-bezier(.34,1.4,.64,1) .06s both}
#aelo-sp .sp-shell[data-state=closing] :is(.sp-backdrop,.sp-card){animation:sp-out .16s ease-in both}
#aelo-sp :is(.sp-primary,.sp-go):active{transform:scale(.985)}
#aelo-sp .sp-cards{scroll-behavior:smooth}
}
@keyframes sp-fade{from{opacity:0}to{opacity:1}}
@keyframes sp-out{from{opacity:1}to{opacity:0}}
@keyframes sp-rise{from{opacity:0;transform:translateY(18px) scale(.97)}to{opacity:1;transform:none}}
@keyframes sp-step{from{opacity:0;transform:translateX(14px)}to{opacity:1;transform:none}}
@keyframes sp-pop{from{opacity:0;transform:scale(.6)}to{opacity:1;transform:none}}
`

const SVG = (body: string, extra = '') =>
  `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"${extra}>${body}</svg>`
const ICONS = {
  close: SVG('<path d="M6 6l12 12M18 6 6 18"/>', ' stroke-width="1.8"'),
  check: SVG('<path d="m5 12.5 4.5 4.5L19 7.5"/>', ' stroke-width="2.4"'),
  arrow: SVG('<path d="M7 17 17 7M9 7h8v8"/>'),
  party: SVG('<path d="M4 20 8.5 8.5l7 7Z"/><path d="M13 5.5c.8-.6 1.6-.6 2.3 0M18.5 9.5c.6-.8.6-1.6 0-2.3M15.5 2.5v1.5M21.5 8.5H20M18.6 5.4l1.1-1.1"/>'),
  door: SVG('<path d="M3 21h18M6 21V4.5A1.5 1.5 0 0 1 7.5 3h9A1.5 1.5 0 0 1 18 4.5V21"/><circle cx="14.5" cy="12.5" r=".9" fill="currentColor"/>'),
  sms: SVG('<path d="M20 12.5a7.5 7.5 0 0 1-11 6.6L4 20.5l1.4-4.6A7.5 7.5 0 1 1 20 12.5Z"/>'),
  email: SVG('<rect x="3" y="5.5" width="18" height="13" rx="1.6"/><path d="m3.6 6.6 8.4 6.4 8.4-6.4"/>'),
  copy: SVG('<rect x="8.5" y="8.5" width="12" height="12" rx="1.5"/><path d="M15.5 8.5V5A1.5 1.5 0 0 0 14 3.5H5A1.5 1.5 0 0 0 3.5 5v9A1.5 1.5 0 0 0 5 15.5h3.5"/>'),
  // "Try another message", as on the share card.
  cycle: SVG('<path d="M20 11a8 8 0 1 0-2.3 5.7"/><path d="M20 4v7h-7"/>'),
  more: GRID,
  whatsapp: BRAND('whatsapp'),
  // Brand glyph from Simple Icons (CC0), as in public/share-icons/; inline, so no blocker can drop the file.
  facebook: '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path fill="#0866FF" d="M9.101 23.691v-7.98H6.627v-3.667h2.474v-1.58c0-4.085 1.848-5.978 5.858-5.978.401 0 .955.042 1.468.103a8.68 8.68 0 0 1 1.141.195v3.325a8.623 8.623 0 0 0-.653-.036 26.805 26.805 0 0 0-.733-.009c-.707 0-1.259.096-1.675.309a1.686 1.686 0 0 0-.679.622c-.258.42-.374.995-.374 1.752v1.297h3.919l-.386 2.103-.287 1.564h-3.246v8.245C19.396 23.238 24 18.179 24 12.044c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.628 3.874 10.35 9.101 11.647Z"/></svg>',
}
// The cookie notice, the privacy controls and the containers they mount in (as in checkEmailDialog.ts).
const PRIVACY = '.aelo-privacy-panel, .aelo-privacy-settings'
const PRIVACY_ROOTS = '#aelo-static-privacy, #aelo-lifestyle-privacy'

function el<K extends keyof HTMLElementTagNameMap>(tag: K, className?: string, text?: string): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag)
  if (className) node.className = className
  if (text !== undefined) node.textContent = text
  return node
}
function button(className: string, text: string, icon?: string): HTMLButtonElement {
  const node = el('button', className)
  node.type = 'button'
  if (icon) node.insertAdjacentHTML('afterbegin', icon)
  node.append(el('span', undefined, text))
  return node
}
function storageGet(storage: 'local' | 'session', key: string): string | null {
  try { return (storage === 'local' ? localStorage : sessionStorage).getItem(key) } catch { return null }
}
function storageSet(storage: 'local' | 'session', key: string, value: string | null) {
  try {
    const store = storage === 'local' ? localStorage : sessionStorage
    if (value === null) store.removeItem(key)
    else store.setItem(key, value)
  } catch { /* Storage blocked: this visit still works. */ }
}
const reducedMotion = () => matchMedia('(prefers-reduced-motion: reduce)').matches

/** One gift card of the ladder: the picture, the count beside the gift, and its total gift value. */
function giftCard(rung: Rung): HTMLLIElement {
  const card = el('li', 'sp-gift')
  card.dataset.count = String(rung.count)
  const head = el('div', 'sp-gift-head')
  const words = el('span')
  words.append(el('span', undefined, `${rung.count} friends`), el('h3', undefined, rung.title))
  head.append(el('span', 'sp-dot', String(rung.count)), words)
  head.firstElementChild!.setAttribute('aria-hidden', 'true')
  const art = el('div', 'sp-gift-art')
  if (rung.image) {
    const image = el('img')
    image.src = rung.image
    image.alt = rung.alt
    image.decoding = 'async'
    art.append(image)
  } else {
    // The $10 Aelo Cash note, drawn here (in navy: no burgundy on the site).
    const note = el('span', 'sp-cash')
    note.setAttribute('aria-hidden', 'true')
    note.append(el('small', undefined, 'Aelo Cash'), el('b', undefined, '$10'), el('i', undefined, 'aelo'))
    art.append(note)
  }
  card.append(art, head)
  if (rung.note) card.append(el('p', undefined, rung.note))
  if (rung.value) {
    const value = el('p', 'sp-value')
    value.append(el('span', 'sp-free', 'Free'), el('span', undefined, rung.value))
    card.append(value)
  }
  return card
}

/** Mounts the row and its pop-up on a research article. Returns the cleanup (a no-op on pages without the row). */
export function mountArticleShare(host: ArticleShareHost): () => void {
  const row = document.querySelector<HTMLElement>('[data-aelo-share-row]')
  const rowButton = row?.querySelector<HTMLButtonElement>('button')
  const slug = document.body.dataset.aeloArticleSlug ?? ''
  if (!row || !rowButton || !/^[a-z0-9-]+$/.test(slug)) return () => {}
  const { signal } = host
  const userAgent = navigator.userAgent
  // Phone or computer, inside an app's own browser or not: which buttons show and what each one does (the share card's rules).
  const env = shareEnvironment(userAgent)
  const main = mainChannels(env) as MainChannel[]
  const headline = (document.querySelector('article h1')?.textContent ?? document.title).trim()
  const pendingKey = `aelo_share_pending_v1:${slug}`
  const countKey = `aelo_share_count_v1:${slug}`

  // ---- the one experiment: the row's line, 50/50 per browser
  const variant: RowVariant = chooseVariant(storageGet('local', VARIANT_KEY), Math.random())
  storageSet('local', VARIANT_KEY, variant)
  const lineNode = row.querySelector<HTMLElement>('[data-aelo-share-line]')
  if (lineNode) lineNode.textContent = ROW_LINES[variant]
  row.dataset.variant = variant
  row.dataset.ready = ''
  const props = (extra: Record<string, unknown> = {}) => ({ variant, article_slug: slug, ...extra })

  // ---- the row hides once invitations close (after October 31, 11:59 p.m. ET, or when the campaign stops)
  const updateRow = () => { row.hidden = invitationsOpen(host.campaign(), host.now()) === false }
  const stopListening = host.onAvailability(updateRow)
  updateRow()

  // ---- seen: once per page view, when at least half of the row is on screen
  let seen = false
  const watcher = 'IntersectionObserver' in window ? new IntersectionObserver(entries => {
    if (seen || !entries.some(entry => entry.isIntersecting)) return
    seen = true
    watcher?.disconnect()
    host.capture('aelo_share_row_seen', props())
  }, { threshold: 0.5 }) : null
  watcher?.observe(row)

  // ---- what the pop-up knows, for as long as the page is open
  let step: Step = 'intro'
  let email = ''
  let isNew = true
  let viaSignIn = false
  let signingIn = false
  // "We just sent an email…": an earlier email's code still works, so the code step shows this note instead of stopping.
  let codeNotice: string | null = null
  let link: string | null = null
  // What is shared once the link is known (the share card's content for this article), and the message on screen now.
  let content: ShareContent | null = null
  let text: ShareText = { id: 'page-popup', text: articleLine(slug, headline) }
  let preview: boolean | null = isLiveHost(location.hostname) ? false : null
  let shares = Number(storageGet('session', countKey)) || 0
  let shown = new Set<Step>()
  let memberCheck = 0
  let leaving: (() => void) | null = null
  let opening = false
  let isOpen = false
  let closeOpen: ((restore: boolean) => void) | null = null

  const style = el('style')
  style.textContent = STYLE
  document.head.append(style)

  // ---- the pop-up, built once and kept: hidden when closed, so the email form (and its security check) never moves
  const hostNode = el('div')
  hostNode.id = 'aelo-sp'
  hostNode.className = 'ph-no-capture'
  hostNode.setAttribute('data-private', '')
  hostNode.hidden = true
  const shell = el('div', 'sp-shell')
  const backdrop = el('div', 'sp-backdrop')
  const card = el('section', 'sp-card')
  card.setAttribute('role', 'dialog')
  card.setAttribute('aria-modal', 'true')
  card.tabIndex = -1
  const close = el('button', 'sp-close')
  close.type = 'button'
  close.setAttribute('aria-label', COPY.close)
  close.innerHTML = ICONS.close
  const previewNote = el('p', 'sp-preview', COPY.previewNote)
  previewNote.hidden = true
  const intro = el('div', 'sp-intro')
  const body = el('div', 'sp-body')
  // The share card's own pop-ups, More (every other app) and Terms (the whole ladder), in a shadow root of their own so they
  // look exactly as on the share card. They open in the browser's top layer, above this pop-up; Esc closes them first.
  const sheetHost = el('div', 'sp-sheets')
  const sheetShadow = sheetHost.attachShadow({ mode: 'open' })
  const sheetStyle = el('style')
  sheetStyle.textContent = SHARE_CARD_STYLE
  const sheetRoot = el('div', 'sc')
  sheetShadow.append(sheetStyle, sheetRoot)
  let sheetEscape: (() => void) | null = null
  const onSheetEscape = (handler: (() => void) | null) => { sheetEscape = handler }
  const moreSheet = cardSheet(sheetRoot, SHARE_CARD_COPY.moreTitle, onSheetEscape)
  const moreList = el('div', 'sc-apps')
  moreSheet.body.append(moreList, el('p', 'sc-note', SHARE_CARD_COPY.moreNote))
  const termsSheet = cardSheet(sheetRoot, TERMS_COPY.title, onSheetEscape)
  termsSheet.body.classList.add('sc-terms-body')
  // "Pick who gets it": back to the next step, the email box before the account exists, the first app button after.
  termsSheet.body.append(...termsContent(() => {
    termsSheet.shut()
    const next = body.hidden ? emailInput : body.querySelector<HTMLElement>('.sp-go')
    next?.focus({ preventScroll: true })
    next?.scrollIntoView({ block: 'nearest' })
  }))
  const openSheet = () => sheetRoot.querySelector<HTMLDialogElement>('dialog[open]')
  const shutSheets = () => { moreSheet.shut(); termsSheet.shut() }
  card.append(close, previewNote, intro, body, sheetHost)
  shell.append(backdrop, card)
  hostNode.append(shell)
  let built = false

  // ---- step 1: the ladder to swipe through, one line, then the email form (bound once to the page's signup adapter)
  const form = el('form', 'sp-form')
  form.id = FORM_ID
  form.noValidate = true
  form.dataset.articleSlug = slug
  const emailInput = el('input')
  const emailSubmit = el('button', 'sp-primary', COPY.emailButton)
  // Signing in uses its own button: the adapter keeps the first one's words in step with its own state.
  const signInSubmit = el('button', 'sp-primary', COPY.signInButton)
  const helper = el('p', 'sp-helper', COPY.emailHelper)
  const ask = el('p', 'sp-ask', COPY.introAsk)
  const switchLine = el('p', 'sp-switch')
  const switchLead = el('span', undefined, COPY.haveAccount)
  const switchButton = button('sp-link', COPY.haveAccountLink)
  {
    const title = el('h2', 'sp-title', COPY.introTitle)
    title.id = 'aelo-sp-intro-title'
    const cards = el('ul', 'sp-cards')
    cards.tabIndex = 0
    cards.setAttribute('aria-label', 'Free gifts for inviting friends')
    for (const rung of LADDER) cards.append(giftCard(rung))
    // Dots under the cards: which gift is showing, and a way to jump to one without swiping.
    const pager = el('div', 'sp-pager')
    const mark = (current: number) => dots.forEach((dot, index) => { if (index === current) dot.setAttribute('aria-current', 'true'); else dot.removeAttribute('aria-current') })
    // A tapped dot stays lit while the cards glide there (passing the others), until the person swipes or scrolls again.
    let tapped: number | null = null
    const dots = LADDER.map((rung, index) => {
      const dot = el('button')
      dot.type = 'button'
      dot.setAttribute('aria-label', `Show the gift for ${rung.count} friends`)
      if (!index) dot.setAttribute('aria-current', 'true')
      dot.addEventListener('click', () => {
        const target = cards.children[index] as HTMLElement | undefined
        if (!target) return
        tapped = index
        mark(index)
        // The last card ends at the right edge (it can't reach the left one); scrolling past the end stops there.
        const left = index === LADDER.length - 1 ? cards.scrollWidth
          : target.offsetLeft - cards.offsetLeft - parseFloat(getComputedStyle(cards).scrollPaddingLeft || '0')
        cards.scrollTo({ left })
      })
      pager.append(dot)
      return dot
    })
    for (const type of ['pointerdown', 'touchstart', 'wheel', 'keydown'] as const) cards.addEventListener(type, () => { tapped = null }, { passive: true })
    cards.addEventListener('scroll', () => {
      if (tapped !== null) { mark(tapped); return }
      // All the way along, the last card shows whole: that is the last gift (Jared, Oct 17: the 4th dot lit the 3rd).
      if (cards.scrollLeft >= cards.scrollWidth - cards.clientWidth - 2) { mark(LADDER.length - 1); return }
      const start = cards.getBoundingClientRect().left + (parseFloat(getComputedStyle(cards).scrollPaddingLeft || '0') || 0)
      let nearest = 0, best = Infinity
      ;[...cards.children].forEach((child, index) => {
        const distance = Math.abs((child as HTMLElement).getBoundingClientRect().left - start)
        if (distance < best) { best = distance; nearest = index }
      })
      mark(nearest)
    }, { passive: true })
    ask.id = 'aelo-sp-intro-line'
    const label = el('label', 'sp-sr', COPY.emailLabel)
    label.htmlFor = `${FORM_ID}-email`
    const signupRow = el('div', 'signup-row')
    emailInput.id = `${FORM_ID}-email`
    emailInput.name = 'email'
    emailInput.type = 'email'
    emailInput.inputMode = 'email'
    emailInput.autocomplete = 'email'
    emailInput.placeholder = COPY.emailLabel
    emailInput.required = true
    emailInput.setAttribute('aria-describedby', `${FORM_ID}-status`)
    emailSubmit.type = 'submit'
    emailSubmit.disabled = true
    signInSubmit.type = 'submit'
    signInSubmit.hidden = true
    signupRow.append(emailInput, emailSubmit, signInSubmit)
    const status = el('p', 'form-status')
    status.id = `${FORM_ID}-status`
    status.setAttribute('role', 'status')
    status.setAttribute('aria-live', 'polite')
    form.append(label, signupRow, helper, status)
    switchButton.addEventListener('click', () => setSignIn(!signingIn))
    switchLine.append(switchLead, switchButton)
    // "Terms" after the deadline line opens the share card's picture ladder (Jared, Oct 18).
    const fine = el('p', 'sp-fine', `${COPY.introFine} `)
    const terms = el('button', 'sp-terms', SHARE_CARD_COPY.terms)
    terms.type = 'button'
    terms.setAttribute('aria-haspopup', 'dialog')
    terms.addEventListener('click', () => {
      termsSheet.open(terms)
      host.capture('aelo_share_terms_opened', props())
    })
    fine.append(terms)
    intro.append(title, cards, pager, el('p', 'sp-note-line', COPY.introNote), ask, form, switchLine, fine)
    // In sign-in mode the form asks for a sign-in link instead of joining: catch the submit before the adapter does.
    intro.addEventListener('submit', event => {
      if (!signingIn || event.target !== form) return
      event.preventDefault()
      event.stopPropagation()
      void signIn()
    }, { capture: true, signal })
  }

  /** "Already have an account? Click here": the same box asks for the email they joined with and sends a sign-in link. */
  function setSignIn(on: boolean) {
    signingIn = on
    ask.textContent = on ? COPY.signInAsk : COPY.introAsk
    helper.textContent = on ? COPY.signInHelper : COPY.emailHelper
    emailSubmit.hidden = on
    signInSubmit.hidden = !on
    switchLead.textContent = on ? COPY.newHere : COPY.haveAccount
    switchButton.querySelector('span')!.textContent = on ? COPY.newHereLink : COPY.haveAccountLink
    const status = form.querySelector<HTMLElement>('.form-status')
    if (status) { status.textContent = ''; delete status.dataset.tone; status.classList.remove('is-error') }
    form.querySelector('.lifestyle-live-actions')?.replaceChildren()
    emailInput.removeAttribute('aria-invalid')
    emailInput.focus()
    if (on) host.capture('aelo_share_step', props({ stage: 'signin' }))
  }
  async function signIn() {
    const status = form.querySelector<HTMLElement>('.form-status')!
    const address = emailInput.value.trim()
    if (!address || !emailInput.validity.valid) {
      emailInput.setAttribute('aria-invalid', 'true')
      status.textContent = 'Please enter a valid email address.'
      status.dataset.tone = 'error'
      emailInput.focus()
      return
    }
    emailInput.removeAttribute('aria-invalid')
    delete status.dataset.tone
    signInSubmit.disabled = true
    if (preview) status.textContent = COPY.sending
    let result: ResendOutcome | null
    try {
      result = preview ? await new Promise<ResendOutcome>(done => window.setTimeout(() => done({ outcome: 'sent' }), 500)) : await host.resend(address)
    } finally { signInSubmit.disabled = false }
    // Not sent (too soon, unknown address and so on): the adapter wrote why on the form, and the person stays here. Its
    // line for an unknown address points to "the form above"; this one's join button is below.
    if (!result || result.outcome !== 'sent') {
      const said = status.textContent ?? ''
      if (said.endsWith(FORM_ABOVE)) status.textContent = `${said.slice(0, -FORM_ABOVE.length)}${COPY.signInUnknown}`
      return
    }
    status.textContent = ''
    email = address
    isNew = false
    viaSignIn = true
    storageSet('session', pendingKey, JSON.stringify({ email, withCode: false, isNew: false }))
    go('back')
  }

  function build() {
    if (built) return
    built = true
    document.body.append(hostNode)
    if (preview) {
      previewNote.hidden = false
      bindPreviewForm()
      return
    }
    host.bindForm(form, {
      onResult: (address, result, warning) => {
        const withCode = result.confirmationCode === true
        // Nothing was sent (bounced, left, blocked): the adapter wrote why on the form, and the person stays here. A
        // code sent minutes ago still works, so "too soon" and "as many as we can" go on to the code box with the note.
        const earlierCode = withCode && (result.link === 'too_soon' || result.link === 'limit')
        if (warning && !earlierCode) return
        email = address
        isNew = result.member !== 'existing'
        viaSignIn = false
        codeNotice = earlierCode ? warning : null
        storageSet('session', pendingKey, JSON.stringify({ email, withCode, isNew }))
        go(withCode ? 'code' : 'back')
      },
      properties: () => ({ variant }),
    })
  }
  /** A deploy preview has no signup service: the same screens, nothing sent, any 6 digits accepted. */
  function bindPreviewForm() {
    const status = form.querySelector<HTMLElement>('.form-status')!
    emailSubmit.disabled = false
    form.addEventListener('submit', event => {
      event.preventDefault()
      const address = emailInput.value.trim()
      if (!address || !emailInput.validity.valid) {
        emailInput.setAttribute('aria-invalid', 'true')
        status.textContent = 'Please enter a valid email address.'
        status.dataset.tone = 'error'
        emailInput.focus()
        return
      }
      emailInput.removeAttribute('aria-invalid')
      delete status.dataset.tone
      status.textContent = 'One moment…'
      emailSubmit.disabled = true
      window.setTimeout(() => {
        status.textContent = ''
        emailSubmit.disabled = false
        email = address
        go('code')
      }, 500)
    }, { signal })
  }

  function track(stage: Step) {
    if (shown.has(stage)) return
    shown.add(stage)
    host.capture('aelo_share_step', props({ stage }))
  }

  // ---- the other steps
  function heading(text: string): HTMLHeadingElement {
    const title = el('h2', 'sp-title', text)
    title.id = 'aelo-sp-title'
    return title
  }
  function lead(text: string, className = 'sp-line'): HTMLParagraphElement {
    const node = el('p', className, text)
    node.id = 'aelo-sp-line'
    return node
  }
  function badge(icon: string): HTMLDivElement {
    const node = el('div', 'sp-badge')
    node.innerHTML = icon
    return node
  }
  function addressLine(parts: readonly string[]): HTMLParagraphElement {
    const node = lead('')
    const address = el('strong')
    for (const part of email.split(/(?<=[@.])/)) address.append(part, document.createElement('wbr'))
    // The full stop stays with the address, never alone at the start of a line.
    const stop = parts[1].match(/^[.,]/)?.[0] ?? ''
    const unit = el('span', 'sp-addr')
    unit.append(address, stop)
    node.append(parts[0], unit, parts[1].slice(stop.length))
    return node
  }

  function codeStep(): Node[] {
    const codeForm = el('form', 'sp-code')
    codeForm.noValidate = true
    const label = el('label', undefined, COPY.codeLabel)
    label.htmlFor = 'aelo-sp-code'
    const input = el('input')
    input.id = 'aelo-sp-code'
    input.type = 'text'
    input.inputMode = 'numeric'
    input.autocomplete = 'one-time-code'
    input.pattern = '[0-9]*'
    input.maxLength = 6
    input.placeholder = '______'
    input.spellcheck = false
    input.setAttribute('aria-describedby', 'aelo-sp-code-status')
    const confirm = el('button', 'sp-primary', COPY.confirm)
    confirm.type = 'submit'
    confirm.disabled = true
    const status = el('p', 'sp-status')
    status.id = 'aelo-sp-code-status'
    status.setAttribute('role', 'status')
    status.setAttribute('aria-live', 'polite')
    codeForm.append(label, input, confirm, status)

    let checking = false
    const showError = (message: string) => {
      status.textContent = message
      status.dataset.tone = 'error'
      input.setAttribute('aria-invalid', 'true')
    }
    const check = async () => {
      const code = cleanCode(input.value)
      if (code.length !== 6 || checking) return
      checking = true
      input.readOnly = true
      confirm.disabled = true
      confirm.textContent = COPY.checking
      delete status.dataset.tone
      status.textContent = ''
      let result: CodeOutcome
      try {
        result = preview
          ? await new Promise<CodeOutcome>(done => window.setTimeout(() => done({ outcome: 'ok' }), 500))
          : await host.verify(email, code)
      } catch { result = { outcome: 'failed', message: COPY.failed } }
      if (!input.isConnected) return
      checking = false
      input.readOnly = false
      confirm.textContent = COPY.confirm
      if (result.outcome === 'ok') { void confirmedNow('welcome'); return }
      if (result.outcome === 'invalid') {
        const left = result.attemptsLeft
        showError(left == null ? COPY.wrongCode : `${COPY.wrongCode} ${left === 1 ? COPY.oneTryLeft : `${left}${COPY.triesLeft}`}`)
      } else if (result.outcome === 'expired') showError(COPY.expired)
      else if (result.outcome === 'limited') showError(COPY.limited)
      else showError(result.message || COPY.failed)
      input.value = ''
      confirm.disabled = true
      input.focus()
    }
    input.addEventListener('input', () => {
      const code = cleanCode(input.value)
      if (code !== input.value) input.value = code
      input.removeAttribute('aria-invalid')
      confirm.disabled = code.length !== 6
      if (code.length === 6) void check()
    })
    codeForm.addEventListener('submit', event => { event.preventDefault(); void check() })

    const nodes: Node[] = [heading(COPY.codeTitle), addressLine(COPY.codeLine)]
    if (codeNotice) nodes.push(el('p', 'sp-note', codeNotice))
    if (preview) nodes.push(el('p', 'sp-note', COPY.previewCode))
    nodes.push(codeForm)
    const inbox = inboxButton(false)
    if (inbox) nodes.push(inbox.node, inbox.note)
    nodes.push(moreLinks(COPY.resendCode))
    return nodes
  }

  /**
   * A member who already confirmed gets a sign-in link, not a code (and, with the code switch off, so does a new one).
   * Asked for through "Already have an account?", whose box already said "Welcome back", it just says to check email.
   */
  function backStep(): Node[] {
    const nodes: Node[] = isNew
      ? [heading(COPY.codeTitle), addressLine(COPY.linkLine)]
      : viaSignIn
        ? [heading(COPY.codeTitle), addressLine(COPY.signInSent)]
        : [badge(ICONS.door), heading(COPY.backTitle), addressLine(COPY.backLine)]
    const inbox = inboxButton(true)
    if (inbox) nodes.push(inbox.node, inbox.note)
    nodes.push(moreLinks(COPY.resendLink))
    return nodes
  }

  /** "Open Gmail" (the mail app inside Facebook's and Instagram's browsers), as in the check-email pop-up. */
  function inboxButton(primary: boolean): { node: HTMLAnchorElement; note: HTMLParagraphElement } | null {
    const inbox = inboxTarget(email, userAgent)
    if (!inbox) return null
    const note = el('p', 'sp-note')
    note.setAttribute('role', 'status')
    const anchor = el('a', primary ? 'sp-primary' : 'sp-secondary')
    anchor.href = inbox.app ?? inbox.url
    if (!inbox.app) { anchor.target = '_blank'; anchor.rel = 'noopener noreferrer' }
    anchor.append(el('span', undefined, inbox.label))
    anchor.insertAdjacentHTML('beforeend', ICONS.arrow)
    if (!inbox.app) anchor.append(el('span', 'sp-sr', ` ${COPY.newTab}`))
    anchor.addEventListener('click', () => {
      host.capture('aelo_check_email_inbox_clicked', props({ source: FORM_ID, destination: inbox.provider }), { sendInstantly: true })
      if (!inbox.app) return
      // The app either opens (this page goes to the background) or nothing happens: then say what to do, here.
      let left = false
      const away = () => { if (document.visibilityState === 'hidden') left = true }
      document.addEventListener('visibilitychange', away)
      window.setTimeout(() => {
        document.removeEventListener('visibilitychange', away)
        if (!left && note.isConnected) note.textContent = COPY.appMissing
      }, 1600)
    })
    return { node: anchor, note }
  }

  /** "Not there? Check Promotions or Spam." then "Send a new code" and "Use a different email". */
  function moreLinks(resendLabel: string): HTMLDivElement {
    const more = el('div', 'sp-more')
    more.append(el('p', undefined, COPY.notThere))
    const resend = button('sp-link', resendLabel)
    const status = el('p', 'sp-status')
    status.setAttribute('role', 'status')
    status.setAttribute('aria-live', 'polite')
    resend.addEventListener('click', async () => {
      if (resend.disabled) return
      resend.disabled = true
      resend.textContent = COPY.sending
      delete status.dataset.tone
      status.textContent = ''
      try {
        const result: ResendOutcome | null = preview ? { outcome: 'sent' } : await host.resend(email)
        if (!result || !resend.isConnected) return
        status.textContent = result.outcome === 'sent' ? COPY.resent : result.message
        if (result.outcome === 'failed') status.dataset.tone = 'error'
      } finally {
        resend.disabled = false
        resend.textContent = resendLabel
      }
    })
    const other = button('sp-link', COPY.differentEmail)
    other.addEventListener('click', () => {
      storageSet('session', pendingKey, null)
      codeNotice = null
      if (!preview) host.differentEmail()
      emailInput.value = ''
      email = ''
      go('intro')
      emailInput.focus()
    })
    more.append(resend, other, status)
    return more
  }

  /** After the code: the ladder they already swiped through, in one glance. */
  function ladderSummary(): HTMLUListElement {
    const list = el('ul', 'sp-sum')
    list.setAttribute('aria-label', 'Your free gifts')
    for (const rung of LADDER) {
      const item = el('li')
      item.append(el('span', 'sp-dot', String(rung.count)), el('span', undefined, rung.short))
      list.append(item)
    }
    return list
  }

  /** The message (one tap copies it all, link included), "Try another message", then the buttons and Copy my link. */
  function shareBlock(withMessage: boolean): HTMLDivElement {
    const wrap = el('div')
    const status = el('p', 'sp-status')
    status.setAttribute('role', 'status')
    status.setAttribute('aria-live', 'polite')
    if (withMessage) {
      const bubble = el('button', 'sp-bubble')
      bubble.type = 'button'
      const words = el('span', undefined, `${text.text} `)
      const label = () => bubble.setAttribute('aria-label', `${COPY.shareLead} ${text.text} ${link ?? ''}`)
      label()
      bubble.append(words, el('span', 'sp-url', link ?? ''))
      bubble.addEventListener('click', () => { void copyMessage(bubble, status) })
      wrap.append(el('p', 'sp-lead', COPY.shareLead), bubble)
      // About three messages per article (Jared, Oct 18): every button sends the one on screen.
      if (content && content.texts.length > 1) {
        const tools = el('div', 'sp-tools')
        const another = button('sp-another', COPY.another, ICONS.cycle)
        another.addEventListener('click', () => {
          if (!content) return
          text = nextText(content.texts, text)
          words.textContent = `${text.text} `
          label()
          // A "Copied" line would now describe the last message.
          delete bubble.dataset.copied
          status.textContent = ''
          host.capture('aelo_share_text_changed', props({ share_text: text.id }))
        })
        tools.append(another)
        wrap.append(tools)
      }
    }
    wrap.append(channelGrid(status), status)
    return wrap
  }

  /** The share card's first buttons (phones: Text, Email, Facebook; computers: Email, Facebook, WhatsApp), then More (the
   * share card's panel of every other app), then Copy my link across the full width. */
  function channelGrid(status: HTMLElement): HTMLDivElement {
    const grid = el('div', 'sp-grid')
    main.forEach((channel, index) => {
      const node = button(`sp-go${index === 0 ? ' is-main' : ''}`, CHANNEL_LABELS[channel], ICONS[channel])
      node.dataset.channel = channel
      node.addEventListener('click', () => sendVia(channel, status))
      grid.append(node)
    })
    // Two first buttons (inside an app's own browser on an iPhone): More takes the whole row, as on the share card.
    const more = button(main.length === 3 ? 'sp-go' : 'sp-go is-wide', CHANNEL_LABELS.more, ICONS.more)
    more.dataset.channel = 'more'
    more.setAttribute('aria-haspopup', 'dialog')
    more.addEventListener('click', () => openMore(more, status))
    const copy = button('sp-go is-wide', CHANNEL_LABELS.copy, ICONS.copy)
    copy.dataset.channel = 'copy'
    copy.addEventListener('click', () => { void copyLinkOnly(status) })
    grid.append(more, copy)
    return grid
  }

  /** "More": every other app in the share card's own pop-up, each with the message on screen, then "All apps" (the phone's own
   * share menu, filled in) where the phone has one (Jared, Oct 18 evening). */
  function openMore(from: HTMLElement, status: HTMLElement) {
    if (!content) return
    const others = shareApps(content, text, env, null).filter(app => !(main as string[]).includes(app.id))
    // The sheet closes first, then the copy: the page under an open sheet can't take the hidden copy box's focus. An app link
    // may take this page away (a browser without tabs), so its close leaves that to the browser.
    moreList.replaceChildren(...others.map(app => appLink(app, chosen => {
      if (chosen.copyOnly) moreSheet.shut()
      else moreSheet.leave()
      useApp(chosen, status)
    })))
    if (hasShareMenu()) moreList.append(allAppsTile(() => content!, () => text, {
      before: () => { moreSheet.shut(); status.textContent = '' },
      // The menu sent it: a share, and "Awesome job!" (as the old share-sheet button did).
      onDone: () => { host.shared('native', shareProps()); counted(); if (isOpen) go('sent') },
      onFailed: () => { void copyMessageFallback(status) },
    }))
    moreSheet.open(from)
  }
  /** The share menu refused (not a person closing it): copy the message instead, to paste anywhere. */
  async function copyMessageFallback(status: HTMLElement) {
    if (!content) return
    status.textContent = await copyToClipboard(messageFor(text, content.link), card) ? COPY.copied : copyFailed()
  }

  function welcomeStep(): Node[] {
    return [badge(ICONS.check), heading(COPY.welcomeTitle), lead(COPY.welcomeLine), ladderSummary(), shareBlock(true)]
  }

  function shareStep(): Node[] {
    return [heading(COPY.shareTitle), shareBlock(true)]
  }

  function sentStep(): Node[] {
    const count = el('p', 'sp-count', `${sentCountText(shares)} `)
    count.append(el('span', undefined, COPY.sentFriends))
    const done = button('sp-link', COPY.done)
    done.addEventListener('click', () => closeOpen?.(true))
    return [badge(ICONS.party), heading(COPY.sentTitle), lead(COPY.sentLine), shareBlock(false), count, done]
  }

  function messageStep(title: string, text: string, page: boolean): Node[] {
    const nodes: Node[] = [heading(title), lead(text)]
    if (page) {
      const anchor = el('a', 'sp-primary', COPY.myPage)
      anchor.href = '/founding/refer'
      nodes.push(anchor)
    }
    return nodes
  }

  function render(next: Step): Node[] {
    switch (next) {
      case 'loading': return [heading(COPY.loading)]
      case 'intro': return []
      case 'code': return codeStep()
      case 'back': return backStep()
      case 'welcome': return welcomeStep()
      case 'share': return shareStep()
      case 'sent': return sentStep()
      case 'locked': return messageStep(COPY.lockedTitle, COPY.locked, true)
      case 'nolink': return messageStep(COPY.noLinkTitle, COPY.noLink, true)
      case 'closed': return messageStep(COPY.closedTitle, COPY.closed, false)
    }
  }

  function go(next: Step) {
    step = next
    if (!isOpen) return
    shutSheets()
    const first = next === 'intro'
    intro.hidden = !first
    body.hidden = first
    if (first) body.replaceChildren()
    else {
      const wrap = el('div', 'sp-step')
      wrap.append(...render(next))
      body.replaceChildren(wrap)
    }
    card.setAttribute('aria-labelledby', first ? 'aelo-sp-intro-title' : 'aelo-sp-title')
    card.setAttribute('aria-describedby', first ? 'aelo-sp-intro-line' : 'aelo-sp-line')
    card.scrollTop = 0
    shell.dataset.fit = 'full'
    fit()
    if (next !== 'loading') track(next)
    // On the first step, focus stays on the card: a phone keyboard would cover the gifts.
    const focus = first ? card
      : next === 'code' ? body.querySelector<HTMLElement>('#aelo-sp-code')
        : body.querySelector<HTMLElement>('.sp-go, .sp-primary, .sp-secondary') ?? card
    focus?.focus({ preventScroll: true })
  }

  // ---- the cookie notice stays visible and usable: the card is laid out above it (as in checkEmailDialog.ts)
  let fullHeight = 0
  function fit() {
    if (!isOpen) return
    let bottom = 0, right = 0
    const rects = (selector: string) => [...document.querySelectorAll<HTMLElement>(selector)].map(node => node.getBoundingClientRect())
      .filter(rect => rect.width && rect.height && rect.top < innerHeight)
    for (const rect of rects('.aelo-privacy-settings')) bottom = Math.max(bottom, innerHeight - rect.top)
    for (const rect of rects('.aelo-privacy-panel')) {
      const cardWidth = card.offsetWidth, cardHeight = Math.min(card.scrollHeight, innerHeight - 32 - bottom)
      const left = (innerWidth - cardWidth) / 2, top = 16 + (innerHeight - 32 - bottom - cardHeight) / 2
      if (left + cardWidth <= rect.left - 16 || left >= rect.right + 16 || top + cardHeight <= rect.top - 16 || top >= rect.bottom + 16) continue
      const roomAbove = rect.top - 32, roomBeside = rect.left - 32
      if (rect.left > 0 && roomAbove < card.scrollHeight && roomBeside >= 320 && roomBeside > roomAbove) {
        right = Math.max(right, Math.max(0, Math.min(innerWidth - rect.left, innerWidth + cardWidth - 2 * (rect.left - 16))))
      } else bottom = Math.max(bottom, innerHeight - rect.top)
    }
    bottom = Math.round(Math.min(bottom, innerHeight * 0.6))
    shell.style.setProperty('--sp-reserve', `${bottom}px`)
    shell.style.setProperty('--sp-reserve-right', `${Math.round(right)}px`)
    if (shell.dataset.fit !== 'tight') fullHeight = card.scrollHeight
    shell.dataset.fit = fullHeight > innerHeight - bottom - 32 ? 'tight' : 'full'
  }

  /** The member's invite link for this article, and what goes with it: the share card's content and messages, the article's
   * own line first. False (and nothing to share) for a link that isn't theirs on aelofilter.com. */
  function useLink(referralUrl: string | null | undefined): boolean {
    link = articleInviteLink(slug, referralUrl)
    const found = link && referralUrl ? shareContentFor({ referralUrl, slug, page: { url: `${SITE}/research/${slug}`, title: headline } }) : null
    if (!link || !found) { link = null; content = null; return false }
    const texts = articleTexts(slug, headline, found.texts)
    content = { ...found, link, texts }
    if (!texts.some(entry => entry.id === text.id && entry.text === text.text)) text = texts[0]
    return true
  }

  // ---- after a correct code, or the email's button in this browser: the member's link, then the next step
  async function confirmedNow(next: 'welcome' | 'share') {
    storageSet('local', MEMBER_HINT_KEY, '1')
    storageSet('session', pendingKey, null)
    const found: Member = preview ? { referralUrl: `https://aelofilter.com/founding?ref=${PREVIEW_CODE}` } : await memberWithRetry()
    if (found === 'locked') { go('locked'); return }
    go(found && useLink(found.referralUrl) ? next : 'nolink')
  }
  async function memberWithRetry(): Promise<Member> {
    const first = await host.member()
    if (first) return first
    // The session cookie from the code arrives with that answer; one more try covers a slow first read.
    await new Promise(done => window.setTimeout(done, 1200))
    return host.member()
  }

  /** Signed in already: straight to the invite link. Not signed in: the sign-up stays (and is never replaced mid-typing). */
  async function checkMember(waiting: boolean) {
    const ticket = ++memberCheck
    const found = await host.member()
    if (ticket !== memberCheck || !isOpen) return
    if (!found) {
      storageSet('local', MEMBER_HINT_KEY, null)
      if (waiting) go('intro')
      return
    }
    if (step === 'intro' && (emailInput.value || document.activeElement === emailInput)) return
    if (step !== 'intro' && step !== 'loading') return
    storageSet('local', MEMBER_HINT_KEY, '1')
    if (found === 'locked') { go('locked'); return }
    go(useLink(found.referralUrl) ? 'share' : 'nolink')
  }

  // ---- sharing
  function counted() {
    shares += 1
    storageSet('session', countKey, String(shares))
  }
  /**
   * Back from the app the message went to (this page was hidden): "Awesome job!". A computer's mail app may not hide it,
   * hence the fallback. A copy has none, and counts as a share only once the person comes back from pasting it.
   */
  function whenBack(fallbackMs: number | null, onBack?: () => void) {
    leaving?.()
    let hidden = false
    const onChange = () => {
      if (document.visibilityState === 'hidden') { hidden = true; return }
      if (hidden) finish()
    }
    const timer = fallbackMs === null ? 0 : window.setTimeout(() => { if (!hidden) finish() }, fallbackMs)
    function stop() {
      document.removeEventListener('visibilitychange', onChange)
      window.clearTimeout(timer)
      if (leaving === stop) leaving = null
    }
    function finish() { stop(); onBack?.(); if (isOpen) go('sent') }
    document.addEventListener('visibilitychange', onChange)
    leaving = stop
  }
  function shareProps() { return { source: 'article_share', variant, article_slug: slug, share_text: text.id } }
  let lastTap = 0
  /** Text, Email, Facebook or WhatsApp, with the message on screen (the share card's links). */
  function sendVia(channel: MainChannel, status: HTMLElement) {
    if (!content || Date.now() - lastTap < 400) return
    lastTap = Date.now()
    const targets = quickTargets(content, text)
    status.textContent = ''
    host.shared(channel, shareProps())
    counted()
    if (channel === 'sms' || channel === 'email') {
      whenBack(1500)
      location.href = targets[channel]
      return
    }
    // Facebook never lets a site fill in the post's words: they are copied inside the tap, to paste. The article's own
    // picture and headline travel with the link. The app opens inside the tap itself (Safari blocks a window opened after
    // waiting on anything); on a computer Facebook opens small, over this page.
    const message = messageFor(text, content.link)
    const copying = channel === 'facebook' ? (copyBySelection(message, card) ? Promise.resolve(true) : copyToClipboard(message, card)) : null
    whenBack(1500)
    const width = 626, height = 560
    const features = env.phone || channel !== 'facebook' ? 'noopener,noreferrer'
      : `noopener,noreferrer,popup=yes,width=${width},height=${height},left=${Math.max(0, Math.round(screenX + (outerWidth - width) / 2))},top=${Math.max(0, Math.round(screenY + (outerHeight - height) / 2))}`
    window.open(targets[channel], '_blank', features)
    void copying?.then(ok => { if (status.isConnected) status.textContent = ok ? SHARE_CARD_COPY.pasteInto.facebook : copyFailed() })
  }
  /**
   * An app from "More". The apps that can't take a message (Messenger, LinkedIn, Instagram, WeChat), and inside an app's own
   * browser the web ones, get it copied inside the tap first, and say where to paste it (the share card's rules and words).
   * Apps that get nothing but the clipboard count once the copy worked and the person comes back from pasting.
   */
  function useApp(app: ShareApp, status: HTMLElement) {
    if (!content) return
    const channel: ShareChannel = SHARE_CHANNEL[app.id] ?? app.id
    const clipboardOnly = Boolean(app.copyOnly) || app.id === 'instagram' || app.id === 'wechat'
    status.textContent = ''
    if (app.copyFirst || app.copyOnly) {
      const message = messageFor(text, content.link)
      const done = (ok: boolean) => {
        if (!status.isConnected) return
        if (!ok) { status.textContent = copyFailed(); return }
        status.textContent = pasteLine(app, env)
        if (clipboardOnly) {
          host.shared(channel, shareProps())
          whenBack(null, counted)
        }
      }
      if (copyBySelection(message, card)) done(true)
      else void copyToClipboard(message, card).then(done)
    }
    if (clipboardOnly) return
    host.shared(channel, shareProps())
    counted()
    whenBack(1500)
  }
  /** One tap on the message copies all of it, link included. Pasted into another app and back again counts as a share. */
  async function copyMessage(bubble: HTMLElement, status: HTMLElement) {
    if (!content) return
    const message = messageFor(text, content.link)
    if (copyBySelection(message, card) || await copyToClipboard(message, card)) {
      status.textContent = COPY.copied
      bubble.dataset.copied = ''
      window.setTimeout(() => { delete bubble.dataset.copied }, 1600)
      host.shared('copy_message', shareProps())
      whenBack(null, counted)
    } else status.textContent = copyFailed()
  }
  async function copyLinkOnly(status: HTMLElement) {
    if (!link) return
    if (await copyToClipboard(link, card)) {
      status.textContent = COPY.linkCopied
      host.shared('copy', shareProps())
      whenBack(null, counted)
    } else status.textContent = copyFailed()
  }
  /** The browser refused the copy: name a button on this screen that needs none (Text shows on phones only). */
  function copyFailed() {
    return env.phone ? COPY.copyFailed : COPY.copyFailedComputer
  }

  // ---- open and close
  async function open() {
    if (isOpen || opening) return
    opening = true
    host.capture('aelo_share_row_opened', props())
    try {
      if (preview === null) preview = !(await serviceAnswers())
    } finally { opening = false }
    build()
    isOpen = true
    hostNode.hidden = false
    shell.dataset.state = 'open'
    shown = new Set()

    // Everything else is inert while it is open (never the privacy controls); restored exactly on close.
    const madeInert: HTMLElement[] = []
    const keepActive = (node: Element) => node === hostNode || node.matches(PRIVACY_ROOTS) || Boolean(node.querySelector(PRIVACY))
    const makeInert = (node: Node) => {
      if (!(node instanceof HTMLElement) || node.inert || keepActive(node) || /^(SCRIPT|STYLE|LINK|META|TEMPLATE|NOSCRIPT)$/.test(node.tagName)) return
      node.inert = true
      madeInert.push(node)
    }
    const lateNodes = new MutationObserver(records => { for (const record of records) record.addedNodes.forEach(makeInert) })
    const privacyWatch = new MutationObserver(() => {
      fit()
      // A cookie choice made with the notice focused removes the notice; focus comes back to the pop-up.
      if (!document.activeElement || document.activeElement === document.body) card.focus({ preventScroll: true })
    })
    for (const node of document.querySelectorAll(PRIVACY_ROOTS)) privacyWatch.observe(node, { childList: true, subtree: true, attributes: true })
    const usable = (node: HTMLElement) => !(node as HTMLButtonElement).disabled && node.getClientRects().length > 0
    function onKey(event: KeyboardEvent) {
      // Esc closes the share card's pop-up (More, Terms) when one is open, else this one.
      if (event.key === 'Escape') { event.preventDefault(); event.stopPropagation(); if (sheetEscape) sheetEscape(); else finish(true); return }
      if (event.key !== 'Tab' || event.altKey || event.ctrlKey || event.metaKey) return
      // Tab cycles through the open sheet's controls; else the pop-up's own, then the cookie choice, which stays usable.
      const sheet = openSheet()
      const items = sheet ? [...sheet.querySelectorAll<HTMLElement>('button, a[href], input, textarea')].filter(usable) : [
        ...[...card.querySelectorAll<HTMLElement>('button, a[href], input, iframe, .sp-cards')].filter(usable),
        ...[...document.querySelectorAll<HTMLElement>(PRIVACY_ROOTS)].flatMap(node => [...node.querySelectorAll<HTMLElement>('button, a[href], input, select, textarea')]).filter(usable),
      ]
      if (!items.length) return
      event.preventDefault()
      const index = items.indexOf((sheet ? sheetShadow.activeElement : document.activeElement) as HTMLElement)
      const next = index < 0 ? (event.shiftKey ? items[items.length - 1] : items[0]) : items[(index + (event.shiftKey ? items.length - 1 : 1)) % items.length]
      next.focus()
    }
    function onFocusIn(event: FocusEvent) {
      const target = event.target
      if (target instanceof Node && hostNode.contains(target)) return
      if (target instanceof Element && target.closest(`${PRIVACY}, ${PRIVACY_ROOTS}`)) return
      card.focus({ preventScroll: true })
    }
    // The email's button, tapped in this browser while the code step waits: back here, the pop-up moves on by itself.
    async function onReturn() {
      if (document.visibilityState !== 'visible' || preview || (step !== 'code' && step !== 'back')) return
      const ticket = ++memberCheck
      const found = await host.member()
      if (ticket !== memberCheck || !isOpen || (step !== 'code' && step !== 'back') || !found) return
      const from = step
      host.confirmed()
      if (found === 'locked') { go('locked'); return }
      storageSet('local', MEMBER_HINT_KEY, '1')
      storageSet('session', pendingKey, null)
      go(useLink(found.referralUrl) ? (from === 'code' ? 'welcome' : 'share') : 'nolink')
    }
    let pressedOutside = false
    const onPointerDown = (event: PointerEvent) => { pressedOutside = !card.contains(event.target as Node) }
    const onClick = (event: MouseEvent) => {
      if (pressedOutside && !card.contains(event.target as Node)) finish(true)
      pressedOutside = false
    }
    const onClose = () => finish(true)
    shell.addEventListener('pointerdown', onPointerDown)
    shell.addEventListener('click', onClick)
    close.addEventListener('click', onClose)

    let closed = false
    // Back = Esc: an open panel (More, Terms) first, then this pop-up. Its history entry is a copy of the page's own state,
    // marked as this pop-up's.
    const entry = ++popupEntries
    const pushEntry = () => history.pushState({ ...(typeof history.state === 'object' ? history.state : null), aeloSharePopup: entry }, '')
    const onBack = () => {
      if (closed) return
      if (!sheetEscape) { onBackRequest = null; finish(true); return }
      sheetEscape()
      try { pushEntry() } catch { /* the next Back leaves the page, as before */ }
    }
    // A same-site link in the pop-up that leaves the page ("Go to my page") takes the pop-up's entry's place, so Back from
    // there comes straight back to the article, not to a second copy of it.
    const onLink = (event: MouseEvent) => {
      const anchor = event.composedPath().find((node): node is HTMLAnchorElement => node instanceof HTMLAnchorElement)
      if (!anchor || event.defaultPrevented || anchor.target === '_blank' || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return
      let url: URL
      try { url = new URL(anchor.href) } catch { return }
      if (url.origin !== location.origin || history.state?.aeloSharePopup !== entry) return
      event.preventDefault()
      onBackRequest = null
      location.replace(url.href)
    }
    function finish(restore: boolean) {
      if (closed) return
      closed = true
      // Closed any other way (the ×, Esc, the backdrop, "Done for now"): take the entry back, so the next Back leaves the
      // article; only while it is still the current entry (never after the page has moved on).
      if (onBackRequest === onBack) { onBackRequest = null; if (history.state?.aeloSharePopup === entry) { ignoredPops += 1; history.back() } }
      card.removeEventListener('click', onLink)
      isOpen = false
      closeOpen = null
      leaving?.()
      shutSheets()
      memberCheck += 1
      document.removeEventListener('keydown', onKey, true)
      document.removeEventListener('focusin', onFocusIn, true)
      document.removeEventListener('visibilitychange', onReturn)
      removeEventListener('resize', fit)
      shell.removeEventListener('pointerdown', onPointerDown)
      shell.removeEventListener('click', onClick)
      close.removeEventListener('click', onClose)
      privacyWatch.disconnect()
      lateNodes.disconnect()
      madeInert.splice(0).forEach(node => { node.inert = false })
      document.body.classList.remove('aelo-sp-open')
      if (restore && rowButton!.isConnected) rowButton!.focus({ preventScroll: true })
      const hide = () => { if (!isOpen) { hostNode.hidden = true; delete shell.dataset.state } }
      if (!restore || reducedMotion()) { hide(); return }
      shell.dataset.state = 'closing'
      shell.addEventListener('animationend', hide, { once: true })
      window.setTimeout(hide, 220)
    }
    closeOpen = finish

    document.body.classList.add('aelo-sp-open')
    for (const node of [...document.body.children]) makeInert(node)
    lateNodes.observe(document.body, { childList: true })
    addEventListener('resize', fit)
    document.addEventListener('keydown', onKey, true)
    document.addEventListener('focusin', onFocusIn, true)
    document.addEventListener('visibilitychange', onReturn)
    card.addEventListener('click', onLink)
    try {
      if (!popListener) {
        addEventListener('popstate', () => { if (ignoredPops) { ignoredPops -= 1; return } onBackRequest?.() })
        popListener = true
      }
      pushEntry()
      onBackRequest = onBack
    } catch { /* no history API here: Back leaves the page, as before */ }

    // Where to start: invitations closed; a link already on this page; a code still waiting; a member; the sign-up.
    if (invitationsOpen(host.campaign(), host.now()) === false) { go('closed'); return }
    if (link) { go(step === 'welcome' ? 'welcome' : 'share'); return }
    if (step === 'code' || step === 'back') { go(step); return }
    const pending = (() => {
      try { return JSON.parse(storageGet('session', pendingKey) ?? 'null') as { email?: unknown; withCode?: unknown; isNew?: unknown } | null } catch { return null }
    })()
    if (!preview && pending && typeof pending.email === 'string' && pending.email.includes('@')) {
      email = pending.email
      isNew = pending.isNew !== false
      go(pending.withCode === false ? 'back' : 'code')
      return
    }
    if (preview) { go('intro'); return }
    // Someone confirmed in this browser before waits a moment for the answer instead of seeing the sign-up first.
    const likelyMember = storageGet('local', MEMBER_HINT_KEY) === '1'
    go(likelyMember ? 'loading' : 'intro')
    void checkMember(likelyMember)
  }

  /** Off the live site only: does a signup service answer here? (Netlify deploy previews have none.) */
  async function serviceAnswers(): Promise<boolean> {
    if (host.campaign()) return true
    if (host.serviceFailed()) return false
    try {
      const response = await fetch('/api/founding/config', { cache: 'no-store', signal: AbortSignal.timeout(4000) })
      return response.ok
    } catch { return false }
  }

  rowButton.addEventListener('click', () => { void open() }, { signal })

  return () => {
    closeOpen?.(false)
    watcher?.disconnect()
    stopListening()
    style.remove()
    hostNode.remove()
  }
}
